<script setup>
import { ref, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { MessageSquareWarning, Loader2 } from 'lucide-vue-next';
import ReportModal from './ReportModal.vue';
import { useScreenCapture } from '../../../composables/useScreenCapture';
import { getConsoleErrors } from '../../../composables/useConsoleBuffer';

const emit = defineEmits(['sent']);

const route = useRoute();
const { captureViewport } = useScreenCapture();

const open = ref(false);
const capturing = ref(false);
const screenshot = ref('');
const context = ref({});
const consoleErrors = ref([]);

async function openReport() {
  if (capturing.value) return;
  capturing.value = true;
  screenshot.value = '';
  try {
    const buildId = typeof __APP_BUILD_ID__ !== 'undefined' ? __APP_BUILD_ID__ : null;
    context.value = {
      routePath: route.fullPath,
      routeName: route.name ? String(route.name) : null,
      userAgent: navigator.userAgent,
      viewport: { w: window.innerWidth, h: window.innerHeight },
      devicePixelRatio: window.devicePixelRatio || 1,
      build: buildId,
    };
    consoleErrors.value = getConsoleErrors();
    // Laisse l'overlay de capture s'afficher (peindre) avant le travail bloquant de domToJpeg.
    await nextTick();
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    screenshot.value = await captureViewport(); // le FAB et l'overlay sont exclus via [data-report-ui]
  } catch (e) {
    console.warn('[report] capture echouee', e);
    screenshot.value = '';
  } finally {
    capturing.value = false;
    open.value = true;
  }
}

function onSent() {
  open.value = false;
  emit('sent');
}
</script>

<template>
  <div>
    <button
      data-report-ui
      type="button"
      class="fixed bottom-24 right-5 z-[9990] w-12 h-12 rounded-full bg-[#e94591] text-white shadow-lg flex items-center justify-center hover:scale-105 active:scale-95 transition-transform disabled:opacity-60"
      :disabled="capturing"
      :title="capturing ? 'Capture en cours...' : 'Signaler un bug ou une suggestion'"
      @click="openReport"
    >
      <Loader2 v-if="capturing" class="w-5 h-5 animate-spin" />
      <MessageSquareWarning v-else class="w-5 h-5" />
    </button>

    <!-- Overlay pendant la capture (exclu du screenshot via data-report-ui) -->
    <div
      v-if="capturing"
      data-report-ui
      class="fixed inset-0 z-[9989] flex items-center justify-center bg-black/30"
    >
      <div class="flex items-center gap-3 bg-white rounded-2xl px-5 py-3 shadow-xl">
        <Loader2 class="w-5 h-5 text-[#e94591] animate-spin" />
        <span class="text-sm font-semibold text-gray-700">Capture de l'écran...</span>
      </div>
    </div>

    <ReportModal
      v-if="open"
      :screenshot="screenshot"
      :context="context"
      :consoleErrors="consoleErrors"
      @close="open = false"
      @sent="onSent"
    />
  </div>
</template>
